import { useState, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  Search, Loader2, Lock, FileWarning, Radar, ShieldCheck, Check,
  ArrowRight, Sparkles, Globe, Server, ScanLine, Wrench,
  Clock, BadgePercent, Zap, CreditCard, RefreshCw, Mail, Send, Activity, Eye,
} from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { ScoreGauge } from "@/components/ScoreGauge";
import { RiskList } from "@/components/RiskList";
import { ShareScore } from "@/components/ShareScore";
import { FixItModal } from "@/components/FixItModal";
import { ExitIntentModal } from "@/components/ExitIntentModal";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const FEATURES = [
  { icon: Lock, title: "SSL & HTTPS validity", desc: "Verifies your certificate is valid, trusted and not about to expire — the padlock customers look for.", span: "md:col-span-7" },
  { icon: Server, title: "Security headers", desc: "Checks HSTS, CSP, X-Frame-Options and other protective headers.", span: "md:col-span-5" },
  { icon: FileWarning, title: "Exposed files", desc: "Finds publicly reachable secrets like .env, .git and backups.", span: "md:col-span-4" },
  { icon: Radar, title: "Breach exposure", desc: "Flags if your domain shows up in known data-breach datasets.", span: "md:col-span-4" },
  { icon: ScanLine, title: "Malware & blacklist", desc: "Detects reputation flags that hurt trust and search ranking.", span: "md:col-span-4" },
  { icon: Sparkles, title: "Plain-English AI report", desc: "Every risk explained by AI — what it is, why it matters, and the exact steps to fix it. No jargon, no guesswork.", span: "md:col-span-12" },
];

const STATS = [
  { value: "6", label: "Security checks per scan" },
  { value: "~30s", label: "Average scan time" },
  { value: "24/7", label: "Continuous monitoring" },
  { value: "0", label: "Data ever sold or shared" },
];

const SCAN_LINES = [
  "SSL certificate — valid, TLS 1.3",
  "Security headers — HSTS, CSP present",
  "Exposed files — no .env / .git found",
  "Breach exposure — checking datasets…",
  "Malware & blacklist — clean",
];

const FAQS = [
  { q: "Is the scan safe for my website?", a: "Completely. Warden only reads publicly available data — no penetration testing, no logins, and zero changes to your site." },
  { q: "Do I need to be technical?", a: "Not at all. Every finding is explained in plain English with step-by-step fixes. And if you'd rather not touch it, our experts can fix it for you." },
  { q: "How long does a scan take?", a: "Usually under a minute. You'll get your Security Score and a full risk breakdown right away." },
  { q: "What if I'm not satisfied?", a: "You're covered by our 30-day money-back guarantee. Cancel anytime from your dashboard — no questions asked." },
  { q: "What does the free trial include?", a: "Full Business features for 7 days, no credit card required. Scan, monitor and export reports before you pay a cent." },
];

const PLANS = [
  { id: "starter", name: "Starter", price: 29, popular: false, features: ["1 website", "Monthly scans", "Full AI report", "Email support"] },
  { id: "business", name: "Business", price: 49, popular: true, features: ["3 websites", "Weekly scans", "Instant alerts", "Priority support"] },
  { id: "agency", name: "Agency", price: 99, popular: false, features: ["10 websites", "Daily scans", "White-label reports", "API access", "Dedicated support"] },
];

const EYEBROW = "text-xs font-mono uppercase tracking-[0.2em] text-cyanbrand";

export default function Landing() {
  const [domain, setDomain] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [fixOpen, setFixOpen] = useState(false);
  const resultRef = useRef(null);
  const { user } = useAuth();
  const navigate = useNavigate();

  const runScan = async (e) => {
    e?.preventDefault();
    if (!domain.trim()) {
      toast.error("Enter a website domain to scan.");
      return;
    }
    setLoading(true);
    setResult(null);
    try {
      const { data } = await api.post("/scan", { domain });
      setResult(data);
      setTimeout(() => resultRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 100);
    } catch (err) {
      const status = err.response?.status;
      if (status === 429) {
        toast.error(formatApiError(err.response?.data?.detail), {
          action: { label: "Sign up free", onClick: () => navigate("/signup") },
          duration: 8000,
        });
      } else {
        toast.error(formatApiError(err.response?.data?.detail) || "Scan failed. Try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen text-white">
      <Navbar />

      {/* HERO — asymmetric split */}
      <section className="relative pt-28 sm:pt-32 pb-16 overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-40" />
        <div className="absolute inset-0 hero-glow" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-10 lg:gap-8 items-center">
          {/* LEFT */}
          <div className="lg:col-span-7">
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="flex flex-wrap items-center gap-3"
            >
              <span className={`inline-flex items-center gap-2 ${EYEBROW}`}>
                <Activity size={13} /> AI-powered security scanner
              </span>
              <span
                data-testid="launch-offer-badge"
                className="inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-wider text-amber-300 border border-amber-400/40 rounded-full px-3 py-1 bg-amber-400/10"
              >
                <Clock size={12} /> Launch week · 30% off · LAUNCH30
              </span>
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.05 }}
              className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05] mt-6"
            >
              Is your website
              <br className="hidden sm:block" /> <span className="text-cyanbrand">actually secure?</span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="mt-6 text-lg text-slate-400 max-w-xl leading-relaxed"
            >
              Warden scans your site for real security risks and explains every finding in plain
              English — no jargon, no login required. Get your Security Score in seconds.
            </motion.p>

            <motion.form
              onSubmit={runScan}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.15 }}
              className="mt-8 max-w-xl"
            >
              <div className="flex flex-col sm:flex-row sm:items-center gap-2 bg-[#0A0F1D] border border-slate-700 rounded-xl p-2 focus-within:border-cyanbrand focus-within:ring-1 focus-within:ring-cyanbrand transition-all">
                <div className="flex items-center flex-1 min-w-0">
                  <Globe size={20} className="text-slate-500 ml-2 sm:ml-3 shrink-0" />
                  <input
                    data-testid="scan-input"
                    value={domain}
                    onChange={(e) => setDomain(e.target.value)}
                    placeholder="yourwebsite.com"
                    className="flex-1 min-w-0 bg-transparent outline-none px-3 py-2 text-white placeholder:text-slate-500 font-mono"
                  />
                </div>
                <button
                  type="submit"
                  data-testid="scan-submit-button"
                  disabled={loading}
                  className="flex items-center justify-center gap-2 bg-cyanbrand hover:bg-[#33ECFF] text-slate-900 font-semibold px-5 py-2.5 rounded-lg transition-all disabled:opacity-60 w-full sm:w-auto shrink-0"
                  style={{ boxShadow: "0 0 20px rgba(0,229,255,0.35)" }}
                >
                  {loading ? <Loader2 size={18} className="animate-spin" /> : <Search size={18} />}
                  {loading ? "Scanning…" : "Scan Free"}
                </button>
              </div>
              <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-slate-400">
                {[[Zap, "Results in ~30 seconds"], [CreditCard, "No credit card"], [ShieldCheck, "100% non-intrusive"], [RefreshCw, "Cancel anytime"]].map(([Ic, t]) => (
                  <span key={t} className="inline-flex items-center gap-1.5">
                    <Ic size={14} className="text-cyanbrand" /> {t}
                  </span>
                ))}
              </div>
            </motion.form>
          </div>

          {/* RIGHT — live scan terminal */}
          <motion.div
            initial={{ opacity: 0, x: 24 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2 }}
            className="lg:col-span-5 hidden md:block"
          >
            <div className="relative rounded-2xl border border-slate-800 bg-[#0A0F1D]/90 backdrop-blur-xl overflow-hidden shadow-[0_20px_60px_rgba(0,0,0,0.5)]">
              <div className="absolute -top-20 -right-20 w-52 h-52 rounded-full bg-cyanbrand/10 blur-3xl" />
              <div className="flex items-center gap-2 px-4 py-3 border-b border-slate-800">
                <span className="w-3 h-3 rounded-full bg-red-500/70" />
                <span className="w-3 h-3 rounded-full bg-amber-400/70" />
                <span className="w-3 h-3 rounded-full bg-green-500/70" />
                <span className="ml-3 font-mono text-xs text-slate-500">warden — live scan</span>
              </div>
              <div className="relative p-5 font-mono text-sm space-y-3">
                <p className="text-slate-500">$ warden scan yourwebsite.com</p>
                {SCAN_LINES.map((line, i) => (
                  <motion.div
                    key={line}
                    initial={{ opacity: 0, x: -8 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.5 + i * 0.35 }}
                    className="flex items-center gap-2 text-slate-300"
                  >
                    <Check size={15} className="text-green-400 shrink-0" />
                    <span className="truncate">{line}</span>
                  </motion.div>
                ))}
                <motion.div
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 2.4 }}
                  className="mt-4 flex items-center justify-between rounded-lg border border-cyanbrand/25 bg-cyanbrand/5 px-4 py-3"
                >
                  <div>
                    <p className="text-[11px] uppercase tracking-widest text-slate-500">Security Score</p>
                    <p className="text-3xl font-bold text-cyanbrand leading-none mt-1">87<span className="text-base text-slate-500">/100</span></p>
                  </div>
                  <div className="text-right text-xs text-slate-400 space-y-0.5">
                    <p><span className="text-orange-400">2</span> high</p>
                    <p><span className="text-amber-400">3</span> medium</p>
                    <p><span className="text-green-400">1</span> low</p>
                  </div>
                </motion.div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* RESULTS */}
      {(loading || result) && (
        <section ref={resultRef} className="relative py-10 border-t border-slate-800/60">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {loading && (
              <div className="flex flex-col items-center py-20 text-slate-400">
                <Loader2 size={40} className="animate-spin text-cyanbrand mb-4" />
                <p className="font-mono text-sm">Running a safe scan of your website…</p>
              </div>
            )}
            {result && <ScanResult result={result} user={user} navigate={navigate} />}
          </div>
        </section>
      )}

      {/* STATS */}
      <section className="py-14 border-y border-slate-800/60 bg-[#060B16]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-2 md:grid-cols-4 gap-8">
          {STATS.map((s, i) => (
            <motion.div
              key={s.label}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.08 }}
              className="text-left"
            >
              <p className="font-heading text-4xl sm:text-5xl font-extrabold text-cyanbrand tracking-tight">{s.value}</p>
              <p className="text-slate-400 text-sm mt-2">{s.label}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* FEATURES — bento */}
      <section className="py-20 sm:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className={EYEBROW}>What we check</p>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight mt-3 max-w-2xl">
            Enterprise-grade checks, small-business simple.
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-12 gap-5 mt-12">
            {FEATURES.map((f, i) => (
              <motion.div
                key={f.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: (i % 3) * 0.06 }}
                className={`${f.span} group bg-[#0A0F1D] border border-slate-800 rounded-2xl p-7 transition-all duration-300 hover:border-cyanbrand/40 hover:shadow-[0_8px_30px_rgba(0,0,0,0.5)]`}
              >
                <div className="w-12 h-12 rounded-xl bg-cyanbrand/10 border border-cyanbrand/25 flex items-center justify-center mb-5 group-hover:scale-105 transition-transform">
                  <f.icon size={24} className="text-cyanbrand" strokeWidth={1.75} />
                </div>
                <h3 className="font-heading font-semibold text-xl">{f.title}</h3>
                <p className="text-slate-400 text-sm mt-2 leading-relaxed max-w-xl">{f.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* PRICING */}
      <section className="py-20 sm:py-28 border-t border-slate-800/60">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl">
            <p className={EYEBROW}>Pricing</p>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight mt-3">
              Protection that fits your business
            </h2>
            <p className="text-slate-400 mt-4">7-day free trial · no card upfront · cancel anytime</p>
            <div className="mt-6">
              <span
                data-testid="promo-callout"
                className="inline-flex items-center gap-2 text-sm font-semibold text-amber-300 border border-amber-400/40 bg-amber-400/10 rounded-full px-5 py-2"
              >
                <BadgePercent size={16} className="shrink-0" /> Launch offer: code <b className="tracking-wider font-mono">LAUNCH30</b> — 30% off your first 3 months
              </span>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-14 items-start">
            {PLANS.map((p) => (
              <div
                key={p.id}
                data-testid={`pricing-${p.id}-tier`}
                className={`relative rounded-2xl p-8 border transition-all duration-300 ${
                  p.popular
                    ? "bg-[#0A0F1D] border-cyanbrand shadow-[0_0_40px_rgba(0,229,255,0.15)] md:-translate-y-3"
                    : "bg-[#0A0F1D] border-slate-800 hover:border-cyanbrand/40"
                }`}
              >
                {p.popular && (
                  <span className="absolute -top-3 left-8 bg-cyanbrand text-slate-900 text-[11px] font-bold uppercase tracking-wider px-3 py-1 rounded-full">
                    Most Popular
                  </span>
                )}
                <h3 className="font-heading text-xl font-semibold">{p.name}</h3>
                <div className="mt-4 flex items-baseline gap-1">
                  <span className="font-heading text-5xl font-extrabold tracking-tight">${p.price}</span>
                  <span className="text-slate-500 font-mono">/mo</span>
                </div>
                <ul className="mt-6 space-y-3">
                  {p.features.map((feat) => (
                    <li key={feat} className="flex items-center gap-2.5 text-sm text-slate-300">
                      <Check size={16} className="text-cyanbrand shrink-0" /> {feat}
                    </li>
                  ))}
                </ul>
                <button
                  onClick={() => navigate(user ? "/billing" : "/signup")}
                  data-testid={`pricing-${p.id}-cta`}
                  className={`mt-8 w-full py-3 rounded-xl font-semibold transition-all ${
                    p.popular
                      ? "bg-cyanbrand hover:bg-[#33ECFF] text-slate-900"
                      : "border border-slate-700 hover:border-cyanbrand text-white"
                  }`}
                  style={p.popular ? { boxShadow: "0 0 20px rgba(0,229,255,0.35)" } : {}}
                >
                  Start free trial
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* GUARANTEE */}
      <section className="py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div
            data-testid="guarantee-band"
            className="flex flex-col sm:flex-row items-center gap-5 bg-gradient-to-br from-[#0A0F1D] to-[#0a1a2e] border border-cyanbrand/25 rounded-2xl p-7 text-center sm:text-left"
          >
            <div className="w-14 h-14 rounded-full bg-cyanbrand/10 border border-cyanbrand/30 flex items-center justify-center shrink-0">
              <ShieldCheck size={28} className="text-cyanbrand" />
            </div>
            <div>
              <h3 className="font-heading text-lg font-semibold">30-day money-back guarantee</h3>
              <p className="text-slate-400 text-sm mt-1">
                Try Warden completely risk-free. If it's not the right fit, we'll refund every cent —
                cancel anytime, no questions asked.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* FIX IT FOR ME */}
      <section className="py-20 sm:py-28 border-t border-slate-800/60">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="relative bg-[#0A0F1D] border border-cyanbrand/30 rounded-3xl p-10 md:p-14 overflow-hidden">
            <div className="absolute inset-0 hero-glow opacity-60" />
            <div className="relative grid md:grid-cols-2 gap-10 items-center">
              <div>
                <p className={EYEBROW}>Done for you</p>
                <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight mt-3">Don't want to fix it yourself?</h2>
                <p className="text-slate-400 mt-4 leading-relaxed">
                  Let Warden's security experts do it for you. One tap and a specialist handles the
                  fixes end-to-end — no jargon, no stress. Perfect for busy business owners.
                </p>
                <button
                  data-testid="fixit-open-button"
                  onClick={() => setFixOpen(true)}
                  className="mt-6 inline-flex items-center gap-2 bg-cyanbrand hover:bg-[#33ECFF] text-slate-900 font-semibold px-6 py-3 rounded-xl transition-all"
                  style={{ boxShadow: "0 0 20px rgba(0,229,255,0.35)" }}
                >
                  <Wrench size={18} /> Fix it for me
                </button>
              </div>
              <div className="space-y-4">
                {[
                  ["We scan & prioritize", "We pinpoint exactly what's putting your site at risk."],
                  ["Experts do the work", "Certified specialists apply the fixes for you."],
                  ["You get a clean report", "Proof your site is secure, in plain English."],
                ].map(([t, d], i) => (
                  <div key={t} className="flex gap-3">
                    <div className="w-8 h-8 rounded-full bg-cyanbrand/10 border border-cyanbrand/30 flex items-center justify-center text-cyanbrand font-heading font-bold text-sm shrink-0">{i + 1}</div>
                    <div>
                      <p className="font-heading font-semibold">{t}</p>
                      <p className="text-slate-400 text-sm">{d}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 sm:py-28 border-t border-slate-800/60">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className={`${EYEBROW} text-center`}>FAQ</p>
          <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight text-center mt-3">Questions, answered</h2>
          <Accordion type="single" collapsible className="mt-10">
            {FAQS.map((f, i) => (
              <AccordionItem key={i} value={`faq-${i}`} className="border-slate-800" data-testid={`faq-${i}`}>
                <AccordionTrigger className="text-left font-heading text-base hover:text-cyanbrand hover:no-underline">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-slate-400 text-sm leading-relaxed">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* TRUST */}
      <section className="py-20 sm:py-24 border-t border-slate-800/60">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl">
            <p className={EYEBROW}>Why trust Warden</p>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight mt-3">Scanning you can trust</h2>
          </div>
          <div className="grid sm:grid-cols-3 gap-6 mt-12">
            {[
              [ShieldCheck, "Non-intrusive by design", "We only read publicly available data — no attacks, no probing, no changes to your site."],
              [Eye, "Your data stays private", "Scan results are tied to your account and never sold or shared with anyone."],
              [Sparkles, "Real findings only", "Our AI explains and prioritizes actual issues. It never invents vulnerabilities."],
            ].map(([Ic, t, d]) => (
              <div key={t} className="bg-[#0A0F1D] border border-slate-800 rounded-2xl p-6 transition-all hover:border-cyanbrand/40">
                <Ic size={22} className="text-cyanbrand mb-4" strokeWidth={1.75} />
                <h4 className="font-heading font-semibold">{t}</h4>
                <p className="text-slate-400 text-sm mt-2 leading-relaxed">{d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <footer className="border-t border-slate-800 py-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2 text-slate-400">
            <ShieldCheck size={18} className="text-cyanbrand" /> <span className="font-heading font-semibold text-white">Warden</span>
            <span className="text-sm">· AI security scanner for small business</span>
          </div>
          <div className="flex items-center gap-6 text-sm text-slate-500">
            <span className="inline-flex items-center gap-2 font-mono text-xs">
              <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse" /> All systems operational
            </span>
            <Link to="/login" className="hover:text-white">Log in</Link>
            <Link to="/signup" className="hover:text-white">Get started</Link>
          </div>
        </div>
      </footer>

      <FixItModal open={fixOpen} onOpenChange={setFixOpen} defaultWebsite={result?.host || domain} />
      <ExitIntentModal />
    </div>
  );
}

function LeadCapture({ host, scanId }) {
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) {
      toast.error("Enter your email to get the full report.");
      return;
    }
    setSending(true);
    try {
      await api.post("/leads/scan-report", { email, host, scan_id: scanId });
      setSent(true);
      toast.success("Report on its way — check your inbox!");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Couldn't send. Try again.");
    } finally {
      setSending(false);
    }
  };

  if (sent) {
    return (
      <div data-testid="lead-capture-sent" className="mt-8 flex items-center gap-3 rounded-2xl border border-green-500/30 bg-green-500/5 p-5">
        <Check size={20} className="text-green-400 shrink-0" />
        <p className="text-sm text-slate-300">Your full report is on its way to <b className="text-white">{email}</b>. We'll also send a friendly reminder if anything needs fixing.</p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} data-testid="lead-capture-form" className="mt-8 rounded-2xl border border-slate-800 bg-[#0A0F1D] p-6">
      <div className="flex items-center gap-2 text-cyanbrand">
        <Mail size={18} />
        <p className="font-heading font-semibold text-white">Email me my full report</p>
      </div>
      <p className="text-slate-400 text-sm mt-1">Get the complete breakdown for {host} in your inbox — plus a heads-up if new risks appear.</p>
      <div className="mt-4 flex flex-col sm:flex-row gap-2">
        <input
          data-testid="lead-email-input"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@business.com"
          className="flex-1 min-w-0 bg-[#060B16] border border-slate-700 rounded-lg px-4 py-2.5 text-white placeholder:text-slate-500 outline-none focus:border-cyanbrand transition-all font-mono text-sm"
        />
        <button
          data-testid="lead-email-submit"
          type="submit"
          disabled={sending}
          className="flex items-center justify-center gap-2 bg-cyanbrand hover:bg-[#33ECFF] text-slate-900 font-semibold px-5 py-2.5 rounded-lg transition-all disabled:opacity-60 shrink-0"
        >
          {sending ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
          {sending ? "Sending…" : "Send report"}
        </button>
      </div>
    </form>
  );
}

function ScanResult({ result, user, navigate }) {
  const c = result.severity_counts || {};
  const total = (c.critical || 0) + (c.high || 0) + (c.medium || 0) + (c.low || 0);
  return (
    <div>
      <div className="grid lg:grid-cols-3 gap-8 items-center bg-[#0A0F1D] border border-slate-800 rounded-2xl p-8">
        <div className="flex flex-col items-center">
          <ScoreGauge score={result.score} grade={result.grade} />
        </div>
        <div className="lg:col-span-2">
          <p className={EYEBROW}>Security Score</p>
          <h2 className="font-heading text-2xl sm:text-3xl font-bold mt-2 font-mono break-all">{result.host}</h2>
          <p className="text-slate-300 mt-4 leading-relaxed" data-testid="ai-summary">{result.ai_summary}</p>
          <div className="flex flex-wrap gap-3 mt-5">
            {["critical", "high", "medium", "low"].map((sev) => (
              <span key={sev} className="text-sm px-3 py-1 rounded-full border border-slate-700 text-slate-300 font-mono">
                <b className="text-white">{c[sev] || 0}</b> {sev}
              </span>
            ))}
            <span className="text-sm px-3 py-1 rounded-full border border-slate-700 text-slate-400 font-mono">{total} issues found</span>
          </div>
          <ShareScore scanId={result.scan_id} host={result.host} score={result.score} />
        </div>
      </div>

      <div className="mt-8 flex items-center justify-between flex-wrap gap-3">
        <h3 className="font-heading text-xl font-semibold">Top risks</h3>
        {!user && (
          <button
            onClick={() => navigate("/signup")}
            data-testid="unlock-report-button"
            className="flex items-center gap-2 bg-cyanbrand hover:bg-[#33ECFF] text-slate-900 font-semibold px-4 py-2 rounded-lg text-sm transition-all"
            style={{ boxShadow: "0 0 15px rgba(0,229,255,0.3)" }}
          >
            Save & monitor this site <ArrowRight size={16} />
          </button>
        )}
      </div>
      <div className="mt-5">
        <RiskList findings={result.findings} explanations={result.ai_explanations} />
      </div>

      {!user && <LeadCapture host={result.host} scanId={result.scan_id} />}

      <div
        data-testid="results-upgrade-cta"
        className="mt-10 relative overflow-hidden rounded-2xl border border-cyanbrand/30 bg-gradient-to-br from-[#0A0F1D] to-[#0a1a2e] p-8 sm:p-10"
        style={{ boxShadow: "0 0 40px rgba(0,229,255,0.12)" }}
      >
        <div className="absolute -top-16 -right-16 w-56 h-56 rounded-full bg-cyanbrand/10 blur-3xl" />
        <div className="relative">
          <p className={EYEBROW}>
            {total > 0 ? `You have ${total} issue${total > 1 ? "s" : ""} to fix` : "Stay protected"}
          </p>
          <h3 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight mt-2 max-w-2xl">
            Fix them, then never worry again.
          </h3>
          <p className="text-slate-300 mt-3 max-w-2xl leading-relaxed">
            Get step-by-step fixes, a downloadable PDF report, and <b className="text-white">24/7 monitoring</b> that
            emails you the moment your score drops. Start free — no card for 7 days.
          </p>
          <div className="grid sm:grid-cols-3 gap-3 mt-6 max-w-2xl">
            {[["Plain-English fixes", "Exactly how to resolve each risk"],
              ["24/7 monitoring + alerts", "Know instantly if anything changes"],
              ["Fix-it-for-me", "Our experts can handle it for you"]].map(([t, d]) => (
              <div key={t} className="flex items-start gap-2">
                <ShieldCheck size={16} className="text-cyanbrand mt-0.5 shrink-0" />
                <div><p className="text-sm text-white font-medium">{t}</p><p className="text-xs text-slate-400">{d}</p></div>
              </div>
            ))}
          </div>
          <div className="flex flex-wrap items-center gap-3 mt-7">
            <button
              onClick={() => navigate(user ? "/billing" : "/signup")}
              data-testid="results-primary-cta"
              className="flex items-center gap-2 bg-cyanbrand hover:bg-[#33EAFF] text-slate-900 font-bold px-6 py-3 rounded-xl text-sm transition-all"
              style={{ boxShadow: "0 0 24px rgba(0,229,255,0.45)" }}
            >
              {user ? "Upgrade & unlock monitoring" : "Start free — 7-day trial"} <ArrowRight size={16} />
            </button>
            <span className="text-xs text-slate-500 font-mono">Cancel anytime · No card required to start</span>
          </div>
        </div>
      </div>
    </div>
  );
}
